import React from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { t, defineMessages } from '@sm/intl';
import { useSurveyTheme, createSurveyStyles } from '@sm/webassets';
// @todo: Will need a custom button to account for `dark` theme
import { Button } from '@sm/wds-react';
import { generateMetricsAttribute } from '@sm/metrics';
import { IconLogoGoldie } from '@wds/icons';

const useThemeStyles = createUseStyles(theme => {
  return {
    footerContainer: {
      textAlign: 'center',
      padding: [theme?.spacing[2], theme?.spacing[4], theme?.spacing[6]],
    },
    footerContent: {
      margin: theme?.spacing[6],
    },
    footerContentCell: {
      display: 'inline-block',
      margin: [theme?.spacing[2], theme?.spacing[3]],
    },
    footerContentLogo: {
      margin: theme?.spacing[3],
      fontSize: theme?.spacing[4],
      fontWeight: theme?.type.fontWeight.regular,
    },
    footerContentText: {
      fontSize: 20,
      fontWeight: theme?.type.fontWeight.regular,
    },
  };
});

const useStyles = createSurveyStyles(theme => {
  return {
    ctaLink: {
      color: theme?.secondaryAccentColor,
      backgroundColor: theme?.primaryAccentColor,
      fontFamily: theme?.questionBody?.fontFamily,
      transition: 'all 0.2s linear',
      '&:hover': {
        borderColor: theme?.primaryAccentColor,
        backgroundColor: theme?.primaryAccentColor,
        boxShadow: `0 0 4px #000060 ${theme?.primaryAccentColor}`,
        outlineOffset: '5px',
        outline: `1px solid ${theme?.primaryAccentColor}`,
      },
    },
  };
});

const DEFAULT_COPY = defineMessages({
  GET_STARTED: {
    id: 'Footer.GetStarted',
    defaultMessage: 'Get Started',
    description: '[Type: Button][Vis.: low] - Get Started Button',
  },
  ALL_SET: {
    id: 'Footer.AllSet',
    defaultMessage: 'All set! Now create your own interactive quiz.',
    description: '[Type: Text][Vis.: low] - All Set Info Message',
  },
});

const Footer = ({ isGrayScale }) => {
  const theme = useSurveyTheme();
  const { ctaLink } = useStyles();
  const { footerContainer, footerContent, footerContentCell, footerContentLogo, footerContentText } = useThemeStyles();
  const isDarkMode = theme.isDark;
  const ctaColor = isDarkMode ? 'black' : 'primary';
  const opacity = isGrayScale && isDarkMode ? 0.5 : 1;
  let goldieLogoColor;
  if (isDarkMode) {
    goldieLogoColor = isGrayScale ? 'lightMuted' : 'light';
  } else {
    goldieLogoColor = isGrayScale ? 'darkMuted' : 'primary'; // account for 'darkMuted' or 'light'
  }

  return (
    <footer className={footerContainer}>
      <div className={footerContent}>
        <div className={footerContentCell}>
          <a
            className={footerContentLogo}
            href="/?ut_source=quiz_results"
            target="_blank"
            rel="noreferrer"
            data-sm-metrics={generateMetricsAttribute({
              data: {
                actionFlow: 'smwebQuizResultsFooterLogo',
                actionType: 'quiz_results_footer_logo_click',
                button: 'quiz_results_footer_logo',
                uiTrigger: 'quiz_results_footer_logo_click_link',
                log: {
                  level: 'info',
                  message: 'smweb quiz results footer logo clicked',
                },
              },
            })}
          >
            <IconLogoGoldie size="xl" color={goldieLogoColor} opacity={opacity} title="SurveyMonkey Logo" />
          </a>
        </div>
        <div className={`${footerContentText} ${footerContentCell}`}>
          <span>{t(DEFAULT_COPY.ALL_SET)}</span>
        </div>
        <div className={footerContentCell}>
          <Button
            data-testid="Footer__QuizCTA"
            size="md"
            className={ctaLink}
            color={ctaColor}
            target="_blank"
            href="/create/?create=scratch&quiz=true&ut_source=quiz_results"
            rel="noreferrer"
            data-sm-metrics={generateMetricsAttribute({
              data: {
                actionFlow: 'smwebQuizResultsFooterCTA',
                actionType: 'quiz_results_cta',
                button: 'quiz_results_footer_cta',
                uiTrigger: 'create_quiz_cta_link',
                log: {
                  level: 'info',
                  message: 'smweb quiz results CTA clicked',
                },
              },
            })}
          >
            {t(DEFAULT_COPY.GET_STARTED)}
          </Button>
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  isGrayScale: PropTypes.bool,
};

Footer.defaultProps = {
  isGrayScale: false,
};

export default Footer;
